import { html } from 'lit';
import { MobxLitElement } from '@adobe/lit-mobx';
import { customElement, property } from 'lit/decorators.js';
import '@digital-realty/ix-icon-button/ix-icon-button.js';
import '@digital-realty/ix-button/ix-button.js';
import { format } from 'date-fns/format.js';
import { TWStyles } from '../../tw.js';
import { Notification } from '../../models/notification.js';
import {
  NotificationGroups,
  SubGroups,
} from '../../constants/notifications.js';
import { NotificationsStyle } from '../../styles/notifications-style.js';
import { NotificationsState } from '../../state/NotificationState.js';

@customElement('view-item-dialog')
export class ViewItemDialog extends MobxLitElement {
  static get styles() {
    return [TWStyles, NotificationsStyle];
  }

  @property({ type: Object, attribute: false }) notification =
    {} as Notification;

  @property({ type: Boolean, attribute: true }) open = false;

  getUrl(): string {
    const ticketId = this.notification.resourceId;

    switch (this.notification.resourceType) {
      case NotificationGroups.PLANNED_MAINTENANCE:
        return `/service-management/reports?ticketId=${ticketId}`;
      case NotificationGroups.SERVICE_TICKETS:
        switch (this.notification.subGroup) {
          case SubGroups.Remote_Hands:
            return `/service-management/service-tickets/remote-hands/${ticketId}`;
          case SubGroups.Customer_Care:
            return `/service-management/service-tickets/customer-care/${ticketId}`;
          case SubGroups.Deliveries:
            return `/service-management/service-tickets/${SubGroups.Deliveries}/${ticketId}`;
          case SubGroups.Facility_Access:
            return `/service-management/service-tickets/facility-access/${ticketId}`;
          case SubGroups.Removals:
            return `/service-management/service-tickets/removals/${ticketId}`;
          case SubGroups.Trouble_Ticket:
            return `/service-management/service-tickets/trouble-tickets/${ticketId}`;
          case SubGroups.FacilityAccessVisitorAccess:
            return `/service-management/service-tickets/facility-access/visitor-access/${ticketId}`;
          case SubGroups.FacilityAccessPermanentBadge:
            return `/service-management/service-tickets/facility-access/permanent-badge/${ticketId}`;
          case SubGroups.FacilityAccessOspAccess:
            return `/service-management/service-tickets/facility-access/osp-access/${ticketId}`;
          case SubGroups.FacilityAccessPopPoeAccess:
            return `/service-management/service-tickets/facility-access/pop-poe-access/${ticketId}`;
          default:
            return '';
        }
      case NotificationGroups.INCIDENTS:
        return `/service-management/reports?ticketId=${ticketId}`;
      case NotificationGroups.DCIM_ALERTS:
        return `/dcim/${ticketId}`;
      default:
        return '';
    }
  }

  getLocation() {
    if (
      this.notification.locations === undefined ||
      this.notification.locations.length < 1
    ) {
      return '';
    }
    if (this.notification.locations.length > 1) {
      return 'Multiple Sites';
    }
    return this.notification.locations[0];
  }

  dialogClosed = () => {
    this.dispatchEvent(new CustomEvent('view-item-dialog-closed'));
  };

  viewRecord = () => {
    this.dialogClosed();
    NotificationsState.showDrawer = false;
  };

  getSubGroupDisplayName() {
    switch (this.notification.subGroup) {
      case SubGroups.FacilityAccessVisitorAccess:
      case SubGroups.FacilityAccessPermanentBadge:
      case SubGroups.FacilityAccessOspAccess:
      case SubGroups.FacilityAccessPopPoeAccess:
        return 'Facility Access';
      case 'Incidents (GSN)':
        return 'Incidents';
      case 'Emergency Repairs (InSite 1.0)':
        return 'Emergency Repairs';
      default:
        return this.notification.subGroup;
    }
  }

  render() {
    return html` <ix-dialog
      ?open=${this.open}
      @closed=${this.dialogClosed}
      class="notification-modal"
      data-testclass="notification-modal"
    >
      <div slot="headline" class="w-[560px]">
        <div class="flex flex-col flex-start">
          <div class="notification-modal-title text-2xl font-bold text-balance">
            ${this.notification.resourceType}
          </div>
          <div
            class="notification-modal-subtitle pt-4 pb-0 font-bold text-balance"
            data-testid="notification-view-item-ticket-type"
          >
            ${this.getSubGroupDisplayName()}
          </div>
        </div>
      </div>
      <form
        class="notification-modal-content"
        slot="content"
        id="form-id"
        method="dialog"
      >
        <div
          class="notification-modal-content-title notification-modal-subject-title"
        >
          Subject
        </div>
        <div
          class="notification-modal-subject-text font-normal text-wrap"
          data-testid="notification-view-item-subject"
        >
          ${this.notification.subject}
        </div>
        <div
          class="notification-modal-content-title notification-modal-created-date-title"
        >
          Create Date
        </div>
        <div
          class="notification-modal-created-date-text font-normal text-wrap"
          data-testid="notification-view-item-date"
        >
          ${format(
            new Date(this.notification.createdAt),
            'dd/MM/yyyy HH:mm:ss'
          )}
        </div>
        <div
          class="notification-modal-content-title notification-modal-site-title"
        >
          Site
        </div>
        <div
          class="notification-modal-site-text font-normal text-wrap"
          data-testid="notification-view-item-site-text"
        >
          ${this.getLocation()}
        </div>
      </form>
      <div slot="actions" class="flex flex-row flex-end">
        <ix-button
          data-testid="notification-view-item-close"
          form="form-id"
          appearance="text"
        >
          Close
        </ix-button>
        <ix-button
          data-testid="notification-view-item-view-record"
          href="${this.getUrl()}"
          @click="${this.viewRecord}"
          appearance="text"
          >View Record</ix-button
        >
      </div>
    </ix-dialog>`;
  }
}
